import React, { useRef, useState, useEffect } from 'react';
import { View, StyleSheet, PanResponder, GestureResponderEvent, PanResponderGestureState, Platform } from 'react-native';

type SeekBarProps = {
  position: number; // Current position in milliseconds
  duration: number; // Total duration in milliseconds
  onSeek: (position: number) => void; // Callback with new position in milliseconds
  onSeekStart?: () => void; // Callback when user starts seeking
  onSeekEnd?: () => void; // Callback when user finishes seeking
};

const SeekBar: React.FC<SeekBarProps> = ({ position, duration, onSeek, onSeekStart, onSeekEnd }) => {
  const [seeking, setSeeking] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);
  const trackRef = useRef<View>(null);
  const containerRef = useRef<View>(null);
  const trackWidth = useRef(0);
  const trackX = useRef(0);
  const currentSeekPosition = useRef(0); // Track current position during drag

  // Calculate progress percentage
  const progress = duration > 0 ? (seeking ? seekPosition : position) / duration : 0;

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,

      onPanResponderGrant: (evt: GestureResponderEvent) => {
        setSeeking(true);
        if (onSeekStart) onSeekStart();

        // Measure track position and width
        trackRef.current?.measure((x, y, width, height, pageX) => {
          trackWidth.current = width;
          trackX.current = pageX;

          // Calculate initial seek position
          const touchX = evt.nativeEvent.pageX - pageX;
          const newProgress = Math.max(0, Math.min(1, touchX / width));
          const newPosition = newProgress * duration;
          setSeekPosition(newPosition);
        });
      },

      onPanResponderMove: (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
        if (trackWidth.current > 0) {
          const touchX = evt.nativeEvent.pageX - trackX.current;
          const newProgress = Math.max(0, Math.min(1, touchX / trackWidth.current));
          const newPosition = newProgress * duration;
          setSeekPosition(newPosition);
        }
      },

      onPanResponderRelease: () => {
        setSeeking(false);
        onSeek(seekPosition);
        if (onSeekEnd) onSeekEnd();
      },

      onPanResponderTerminate: () => {
        setSeeking(false);
        if (onSeekEnd) onSeekEnd();
      },
    })
  ).current;

  // Web-specific event handlers using Pointer Events API
  // Attach directly to DOM element for better compatibility
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Try multiple methods to get the DOM element
    const container = containerRef.current as any;
    if (!container) return;

    // Try different ways to get the underlying DOM element
    let element: HTMLElement | null = null;
    if (typeof container.getNode === 'function') {
      element = container.getNode();
    } else if (container._nativeTag) {
      element = document.querySelector(`[data-tag="${container._nativeTag}"]`);
    } else {
      element = container;
    }

    // Last resort - find the element by looking for our specific styles
    if (!element || !element.addEventListener) {
      const allDivs = Array.from(document.querySelectorAll('div'));
      for (const div of allDivs) {
        const style = window.getComputedStyle(div);
        if (style.flex === '1' && div.querySelector('[style*="background"]')) {
          element = div;
          break;
        }
      }
    }

    if (!element || !element.addEventListener) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!element) return;

      event.preventDefault();
      event.stopPropagation();

      setSeeking(true);
      if (onSeekStart) onSeekStart();

      const rect = element.getBoundingClientRect();

      const getPosition = (e: PointerEvent) => {
        const x = e.clientX - rect.left;
        const progress = Math.max(0, Math.min(1, x / rect.width));
        return progress * duration;
      };

      const initialPosition = getPosition(event);
      currentSeekPosition.current = initialPosition;
      setSeekPosition(initialPosition);

      const handlePointerMove = (e: PointerEvent) => {
        e.preventDefault();
        const movePosition = getPosition(e);
        currentSeekPosition.current = movePosition;
        setSeekPosition(movePosition);
      };

      const handlePointerUp = () => {
        setSeeking(false);
        onSeek(currentSeekPosition.current);
        if (onSeekEnd) onSeekEnd();
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
    };

    element.addEventListener('pointerdown', handlePointerDown);
    element.style.cursor = 'pointer';
    element.style.touchAction = 'none';

    return () => {
      if (element) {
        element.removeEventListener('pointerdown', handlePointerDown);
      }
    };
  }, [duration, onSeek, onSeekStart, onSeekEnd]);

  return (
    <View
      ref={containerRef}
      style={styles.container}
      {...(Platform.OS !== 'web' ? panResponder.panHandlers : {})}
    >
      <View
        ref={trackRef}
        style={styles.track}
      >
        <View style={[styles.progress, { width: `${progress * 100}%` }]} />
        <View style={[styles.thumb, { left: `${progress * 100}%` }]} />
      </View>
    </View>
  );
};

const TRACK_HEIGHT = 4;
const THUMB_SIZE = 14;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 10, // Increase touch area
  },
  track: {
    height: TRACK_HEIGHT,
    backgroundColor: '#666666',
    borderRadius: TRACK_HEIGHT / 2,
    position: 'relative',
    overflow: 'visible',
  },
  progress: {
    position: 'absolute',
    height: TRACK_HEIGHT,
    backgroundColor: '#33e6ff',
    borderRadius: TRACK_HEIGHT / 2,
  },
  thumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    backgroundColor: '#ffffff',
    top: (TRACK_HEIGHT - THUMB_SIZE) / 2,
    marginLeft: -THUMB_SIZE / 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 3,
  },
});

export default SeekBar;
